import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Briefcase, Users, UserRound, ArrowRight, Mic, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { fetchAmbientObservation } from "@/lib/behavioralContinuity";
import { fetchArchetypeMemoryLine } from "@/lib/archetypes";
import RecentSessionsModule from "@/components/RecentSessionsModule";
import AmbientMemoryStrip from "@/components/AmbientMemoryStrip";
import SEO from "@/components/SEO";

type Scenario = "relationship" | "workplace" | "family" | "friendship";

const SCENARIOS: { key: Scenario; label: string; icon: typeof Heart; entry_type: string }[] = [
  { key: "relationship", label: "Relationship", icon: Heart, entry_type: "personal" },
  { key: "workplace", label: "Workplace", icon: Briefcase, entry_type: "professional" },
  { key: "family", label: "Family", icon: Users, entry_type: "personal" },
  { key: "friendship", label: "Friendship", icon: UserRound, entry_type: "personal" },
];

const SCENARIO_PATTERNS: Record<Scenario, { name: string; score: number }[]> = {
  relationship: [{ name: "Gaslighting", score: 7 }, { name: "Guilt Induction", score: 6 }],
  workplace: [{ name: "Passive Aggression", score: 6 }, { name: "Credit Theft", score: 5 }],
  family: [{ name: "Guilt Induction", score: 7 }, { name: "Triangulation", score: 6 }],
  friendship: [{ name: "Passive Aggression", score: 5 }, { name: "Exclusion", score: 5 }],
};

const POPULAR_PATTERNS = [
  "Gaslighting",
  "DARVO",
  "Guilt Induction",
  "Stonewalling",
  "Triangulation",
  "Love Bombing",
];

const TRENDING_PHRASES = [
  "You're too sensitive.",
  "You always overreact.",
  "I was just joking.",
  "You're remembering it wrong.",
  "Why are you making this a big deal?",
];


const SpeechRecognitionAPI = typeof window !== "undefined"
  ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  : null;

const Practice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [scenario, setScenario] = useState<Scenario>("relationship");
  const [input, setInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const recognitionRef = useRef<any>(null);
  const speechSupported = !!SpeechRecognitionAPI;
  const [ambientObservation, setAmbientObservation] = useState<string | null>(null);
  const [archetypeMemory, setArchetypeMemory] = useState<string | null>(null);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  useEffect(() => {
    if (!user) return;
    fetchAmbientObservation(user.id).then(setAmbientObservation).catch(() => {});
    fetchArchetypeMemoryLine(user.id).then(setArchetypeMemory).catch(() => {});
  }, [user]);

  const toggleMic = () => {
    if (!SpeechRecognitionAPI) return;
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }
    const r = new SpeechRecognitionAPI();
    r.lang = "en-US";
    r.interimResults = false;
    r.continuous = false;
    recognitionRef.current = r;
    r.onresult = (e: any) => setInput((p) => (p ? p + " " : "") + e.results[0][0].transcript);
    r.onend = () => setIsListening(false);
    r.onerror = () => setIsListening(false);
    r.start();
    setIsListening(true);
  };

  const handleStart = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    const meta = SCENARIOS.find((s) => s.key === scenario)!;
    setIsLaunching(true);
    window.setTimeout(() => {
      navigate("/rehearsal", {
        state: {
          entry_type: meta.entry_type,
          scenario,
          patterns: SCENARIO_PATTERNS[scenario],
          seed_context: input.trim() || undefined,
        },
      });
    }, 850);
  };

  const handlePhraseClick = (phrase: string) => setInput(phrase);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 8%) 0%, hsl(0 0% 4%) 100%)" }}
    >
      <SEO
        title="Practice the conversation before it happens | GreyRock AI"
        description="Simulate manipulation scenarios with an AI sparring partner. Practice grey rock, boundary-setting, and de-escalation before the conversation actually happens."
        path="/practice"
      />

      <IntelligenceTopBar />

      <div className="flex-1 max-w-3xl mx-auto w-full px-4 pt-16 md:pt-24 pb-32 space-y-28">
        {/* HERO SIMULATION CARD */}
        <section className="relative">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 blur-3xl opacity-40"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 30%, hsl(180 100% 50% / 0.12), transparent 70%)",
            }}
          />

          <div className="text-center mb-10 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan/5 border border-cyan/15">
              <Sparkles className="w-3 h-3 text-cyan/80" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-cyan/80 font-mono">
                Behavioral Rehearsal
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1]">
              <span className="text-foreground">Practice the conversation </span>
              <span className="text-gradient-cyan">before it happens.</span>
            </h1>
            <p className="text-sm md:text-base text-muted-foreground/80 max-w-lg mx-auto leading-relaxed">
              AI-powered behavioral rehearsal for difficult conversations.
            </p>
          </div>

          <div className="rounded-2xl border border-cyan/15 bg-card/60 backdrop-blur-md p-6 md:p-8 shadow-[0_0_80px_-30px_hsl(180_100%_50%_/_0.4)]">
            <label className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono mb-3">
              What are they likely to say?
            </label>

            <div className="relative">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`e.g. "You're overreacting again. I never said that."`}
                className="min-h-[130px] pr-12 bg-secondary/40 border-border/60 text-foreground placeholder:text-muted-foreground/50 resize-none rounded-xl text-base leading-relaxed"
              />
              {speechSupported && (
                <button
                  type="button"
                  onClick={toggleMic}
                  className={cn(
                    "absolute right-3 top-3 p-2 rounded-lg transition-all",
                    isListening
                      ? "text-cyan bg-cyan/10 animate-pulse"
                      : "text-muted-foreground/70 hover:text-foreground hover:bg-secondary"
                  )}
                  title={isListening ? "Stop dictation" : "Dictate"}
                >
                  <Mic className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-mono mb-3">
                Scenario
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SCENARIOS.map(({ key, label, icon: Icon }) => {
                  const active = scenario === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setScenario(key)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all",
                        active
                          ? "border-cyan/40 bg-cyan/10 text-cyan shadow-[0_0_20px_-8px_hsl(180_100%_50%_/_0.5)]"
                          : "border-border/50 bg-secondary/30 text-muted-foreground hover:text-foreground/90 hover:border-border"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={handleStart}
              size="lg"
              className={cn(
                "mt-7 w-full h-14 rounded-xl text-base font-semibold tracking-wide",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.01] transition-transform shadow-[0_0_40px_-10px_hsl(180_100%_50%_/_0.7)]"
              )}
            >
              Start Simulation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground/40 font-mono text-center mt-4">
              AI Rehearsal Session · 2 Credits
            </p>
          </div>
        </section>

        {/* AMBIENT MEMORY STRIP — observed continuity across recent rehearsals */}
        <section className="-mt-12">
          <AmbientMemoryStrip />
        </section>

        {/* AMBIENT BEHAVIORAL OBSERVATION — surfaces only after enough rehearsal history */}
        {ambientObservation && (
          <section className="-mt-16">
            <div className="mx-auto max-w-md text-center space-y-2 opacity-80">
              <p className="text-[9px] font-mono tracking-[0.32em] uppercase text-cyan/55">
                Something we've noticed
              </p>

              <p className="text-sm text-foreground/70 italic font-light leading-relaxed">
                "{ambientObservation}"
              </p>
              <div className="h-px w-12 mx-auto bg-gradient-to-r from-transparent via-cyan/25 to-transparent" />
            </div>
          </section>
        )}

        {/* AMBIENT DIVIDER */}
        <div className="flex items-center justify-center">
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-border/50 to-transparent" />
        </div>

        {/* BEHAVIORAL ARCHETYPES — subordinate */}
        <section className="opacity-80 hover:opacity-100 transition-opacity duration-500">
          <h2 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 font-mono text-center mb-6">
            Patterns to practice against
          </h2>

          <div className="flex flex-wrap justify-center gap-x-5 gap-y-3">
            {POPULAR_PATTERNS.map((name) => (
              <button
                key={name}
                onClick={() => {
                  if (!user) return setShowAuth(true);
                  navigate("/rehearsal", {
                    state: {
                      entry_type: SCENARIOS.find((s) => s.key === scenario)!.entry_type,
                      scenario,
                      patterns: [{ name, score: 7 }],
                    },
                  });
                }}
                className="text-xs font-medium text-muted-foreground/80 hover:text-cyan transition-colors tracking-wide"
              >
                {name}
              </button>
            ))}
          </div>

          {/* Archetype memory + archive entry — quietly observational */}
          <div className="mt-8 text-center space-y-2">
            {archetypeMemory && (
              <p className="text-[11px] text-foreground/55 italic font-light leading-relaxed max-w-md mx-auto">
                {archetypeMemory}
              </p>
            )}
            <button
              onClick={() => navigate("/archetypes")}
              className="text-[10px] font-mono tracking-[0.28em] uppercase text-cyan/60 hover:text-cyan transition-colors"
            >
              Browse all patterns →
            </button>

          </div>
        </section>

        {/* AMBIENT TWO-COLUMN — archival */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-10 opacity-70 hover:opacity-95 transition-opacity duration-500">
          <div>
            <RecentSessionsModule limit={4} />
          </div>

          <div>
            <h2 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/50 font-mono mb-5">
              Trending Phrases
            </h2>
            <div className="space-y-2.5">
              {TRENDING_PHRASES.map((phrase) => (
                <button
                  key={phrase}
                  onClick={() => handlePhraseClick(phrase)}
                  className="block w-full text-left transition-all duration-300 group"
                >
                  <span className="text-xs text-foreground/45 italic group-hover:text-cyan/90 group-hover:[text-shadow:0_0_12px_hsl(184_100%_50%_/_0.4)] transition-all">
                    "{phrase}"
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>

      <AuthModal open={showAuth} onOpenChange={setShowAuth} />

      {/* CINEMATIC LAUNCH OVERLAY */}
      {isLaunching && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 50%, hsl(0 0% 4% / 0.85), hsl(0 0% 2% / 0.98))",
            animation: "fadeIn 0.45s ease-out forwards",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-x-0 top-1/2 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(184 100% 50% / 0.6), transparent)",
              boxShadow: "0 0 40px hsl(184 100% 50% / 0.5)",
              animation: "launchSweep 0.85s ease-out forwards",
            }}
          />
          <div className="text-center space-y-3 opacity-0" style={{ animation: "fadeIn 0.5s ease-out 0.2s forwards" }}>
            <div className="text-[10px] tracking-[0.4em] uppercase text-cyan/80 font-mono">
              Entering practice
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground/50 font-mono">
              Setting up your practice space
            </div>

          </div>
          <style>{`
            @keyframes fadeIn { to { opacity: 1; } }
            @keyframes launchSweep {
              0% { transform: scaleX(0); opacity: 0; }
              40% { opacity: 1; }
              100% { transform: scaleX(1); opacity: 0.8; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Practice;
